import React, { useState, useEffect } from 'react';
import {
    Box,
    FormControl,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
} from '@mui/material';
import axios from 'axios';
import DashboardMap from './DashboardMap';
import LiveIcon from "../../images/dashboard/map_live.svg";
import './Dashboard.css';
import config from "../../config.json";
import { useTranslation } from 'react-i18next';
export default function DashboardMapFilter() {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    const [selectedRoutes, setSelectedRoutes] = useState([]);
    const [data, setData] = useState({
        vehicles: [],
        routes: [],
        stops: [],
        users: []
    });

    // Filtered data state
    const [filteredStops, setFilteredStops] = useState([]);
    const [filteredRoutes, setFilteredRoutes] = useState([]);
    const [filteredVehicles, setFilteredVehicles] = useState([]);

    // API Calls
    const getAllVehicles = async () => {
        try {
            const resp = await axios.get(`${config.baseURL}/vehicle/organization`, {
                withCredentials: true,
            });
            return resp.data;
        } catch (error) {
            handleApiError(error);
            return [];
        }
    };

    const getAllRoutes = async () => {
        try {
            const resp = await axios.get(`${config.baseURL}/route/organization`, {
                withCredentials: true,
            });
            return resp.data;
        } catch (error) {
            handleApiError(error);
            return [];
        }
    };

    const getAllStops = async () => {
        try {
            const resp = await axios.get(`${config.baseURL}/stop/organization`, {
                withCredentials: true,
            });
            return resp.data;
        } catch (error) {
            handleApiError(error);
            return [];
        }
    };

    const handleApiError = (error) => {
        console.error('API Error:', error);
        // Implement your error handling logic here
    };

    // Handle route selection change
    const handleRouteChange = (event) => {
        const selectedValues = event.target.value;
        setSelectedRoutes(selectedValues);
        filterDataByRoutes(selectedValues);
    };

    // Filter data based on selected routes
    const filterDataByRoutes = (selectedRouteIds) => {
        if (selectedRouteIds.length === 0) {
            // If no routes selected, show all data
            setFilteredRoutes(data.routes);
            setFilteredStops(data.stops);
            setFilteredVehicles(data.vehicles);
            return;
        }

        // Filter routes
        const routes = data.routes.filter(route =>
            selectedRouteIds.includes(route.id.toString())
        );
        setFilteredRoutes(routes);

        // Filter stops using the coordinate comparison approach
        const stops = data.stops.filter(stop => {
            return selectedRouteIds.some(routeId => {
                const selectedRouteObj = data.routes.find(r => r.id.toString() === routeId);
                return selectedRouteObj?.routeCoordinates?.some(coord =>
                    coord.latitude === stop.latitude && coord.longitude === stop.longitude
                );
            });
        });

        setFilteredStops(stops);

        // Filter vehicles - safely check for routeId property
        const vehicles = data.vehicles.filter(vehicle =>
            vehicle.routeId && selectedRouteIds.includes(vehicle.routeId.toString())
        );

        setFilteredVehicles(vehicles);
    };
    // Fetch initial data
    const fetchInitialData = async () => {
        setIsLoading(true);
        try {
            const [vehicles, routes, stops] = await Promise.all([
                getAllVehicles(),
                getAllRoutes(),
                getAllStops()
            ]);

            const initialData = {
                vehicles,
                routes,
                stops,
                users: [] // If you need users data, add it here
            };

            setData(initialData);

            // Initialize filtered data with all data
            setFilteredVehicles(vehicles);
            setFilteredRoutes(routes);
            setFilteredStops(stops);
        } catch (error) {
            handleApiError(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchInitialData();
    }, []);

    return (
        <Box sx={{ position: 'relative', height: '100%' }}>
            {/* Header container with Live indicator and Select box */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 50,
                    right: 50,
                    left: 50,
                    zIndex: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                {/* Live Map indicator */}
                <Box
                    sx={{
                        backgroundColor: 'white',
                        padding: '8px 16px',
                        borderRadius: 1,
                        boxShadow: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <img
                        src={LiveIcon}
                        alt="Live Map"
                        style={{
                            width: 20,
                            height: 20,
                            animation: "pulse 1.5s infinite"
                        }}
                    />
                    <span style={{ fontSize: '12px' }}>{t("liveMap")}</span>
                </Box>

                {/* Select Box */}
                <Box
                    sx={{
                        width: 200,
                        backgroundColor: 'white',
                        borderRadius: 1,
                        boxShadow: 2
                    }}
                >
                    <FormControl size="small" fullWidth>
                        <Select
                            multiple
                            displayEmpty
                            value={selectedRoutes}
                            onChange={handleRouteChange}
                            renderValue={(selected) => {
                                if (selected.length === 0) {
                                    return <em>{t("selectRoutes")}</em>;
                                }
                                return `${selected.length} routes selected`;
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        maxHeight: 200,
                                        overflowY: 'auto',
                                        borderRadius: '8px',
                                        '&::-webkit-scrollbar': {
                                            width: '4px',
                                            backgroundColor: ' #B7BFCC',
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            backgroundColor: ' #647490',
                                            borderRadius: '4px',
                                        },
                                        '&::-webkit-scrollbar-thumb:hover': {
                                            backgroundColor: '#555',
                                        }
                                    }
                                }
                            }}
                        >
                            <MenuItem disabled value="">
                                <em>{t("selectRoutes")}</em>
                            </MenuItem>
                            {data.routes.map((route) => (
                                <MenuItem
                                    key={route.id}
                                    value={route.id.toString()}
                                    sx={{
                                        fontSize: '10px',
                                        padding: '6px 8px',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    <Checkbox
                                        checked={selectedRoutes.includes(route.id.toString())}
                                        size="small"
                                        sx={{ padding: '4px' }}
                                    />
                                    <ListItemText
                                        primary={route.name || `Route ${route.id}`}
                                        sx={{
                                            fontSize: '10px',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: '150px'
                                        }}
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {/* Map Container */}
            <Box sx={{ height: '100%', padding: "0px" }}>
                {isLoading ? (

                    <div style={{ padding: "40px" }}>
                        <div height={400} width="100%" />
                    </div>

                ) : (
                    <DashboardMap
                        stops={filteredStops}
                        routes={filteredRoutes}
                        vehicles={filteredVehicles}
                    />
                )}
            </Box>
        </Box>
    );
}

